import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Users, Star, Clock, UserPlus, MapPin, X, Loader2, CheckCircle2,
  Plus, Search, ChevronRight, Phone, Calendar, Award,
  TrendingDown, TrendingUp, BarChart3, RefreshCw, User, Briefcase,
  ClipboardList, Settings, Medal, Zap, FileText, Trash2, ArrowRightLeft,
  Gift, AlertCircle, Check, MoreHorizontal, Hash, DollarSign, Send, ListTodo,
} from "lucide-react";
import TareasPanel from "../components/TareasPanel";   // Fix DO (2026-09-22)
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { formatPYGCompact, formatPYG, formatDate } from "../utils/format";
import { toast } from "sonner";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../contexts/AuthContext";
import { roleAllowed } from "../utils/roles";
import ParteDiarioAdminTab from "./hr/ParteDiarioAdminTab";
import ParteDiarioObraTab from "./hr/ParteDiarioObraTab";
import IpsComparacionTab from "./hr/IpsComparacionTab";
import MarcacionTab from "./hr/MarcacionTab";   // Fix GW (2026-10-19): marcación con geolocalización
import TurnosModal from "./hr/TurnosModal";   // Fix ES (2026-09-30)
import SelectorEmpleados from "./hr/SelectorEmpleados";   // Fix GB (2026-10-14): los filtros del Parte Diario, en Liquidación
import SelectorCargos from "./hr/SelectorCargos";
import { useTurnos, filtrarEmpleados, SelectTurno } from "./hr/FiltroTurnoObra";
import AdelantosDescuentosTab, { AprobadoresAdelantosCard } from "./hr/AdelantosDescuentosTab";   // Fix FJ
import { exportarTablaPdf, fechaReporte } from "../utils/reportes";   // Fix FJ
import MatrizProrrateo, { useMatrizProrrateo, PRORRATEO_ROLES } from "./hr/MatrizProrrateo";   // Fix GD (2026-10-14): liquidación del CEN Operativo
import { useIngresosObras } from "./hr/IngresosObras";

// ─── CONSTANTS ────────────────────────────────────────────
const CARGO_OPTIONS = [
  "Ingeniero Residente", "Coordinador de Obra", "Jefe de Cuadrilla",
  "Operario Electricista", "Ayudante de Obra", "Técnico Instalador",
  "Inspector Eléctrico", "Almacenero", "RRHH", "Administrador",
  "Contador", "Jefe de Compras", "Otro",
];
const STATUS_OPTIONS = ["activo", "inactivo", "licencia"];
const STATUS_BADGE = {
  activo: "bg-emerald-100 text-emerald-700",
  inactivo: "bg-gray-100 text-gray-600",
  licencia: "bg-amber-100 text-amber-700",
};
const TABS = [
  { id: "tareas", label: "Tareas", icon: ListTodo },   // Fix DO (2026-09-22)
  { id: "personal", label: "Personal", icon: Users },
  { id: "parte_admin", label: "Parte Diario Adm.", icon: ClipboardList, roles: ["Superadministrador", "Administrador", "Jefe de RRHH"] },
  // Fix GW (2026-10-19): "un apartado de marcación de los personales con geolocalización" (CEN).
  { id: "marcacion", label: "Marcación", icon: MapPin, roles: ["Superadministrador", "Administrador", "Jefe de RRHH", "Director", "Gerente Tecnico", "Coordinador de Obra", "Residente de Obra"] },
  { id: "parte_obra", label: "Parte Diario Obra", icon: ClipboardList, roles: ["Superadministrador", "Administrador", "Gerente Tecnico", "Residente de Obra", "Coordinador de Obra"] },
  { id: "adelantos", label: "Adelantos / Descuentos", icon: DollarSign },   // Fix FJ
  { id: "desempeno", label: "Desempeño", icon: Star },
  { id: "liquidacion", label: "Liquidación", icon: Send },
  { id: "ips", label: "IPS vs Partes", icon: FileText, roles: ["Superadministrador", "Administrador", "Jefe de RRHH", "Contador"] },
  { id: "config", label: "Config HR", icon: Settings },
];
const EVENT_CATEGORIES_POS = [
  { id: "asistencia", label: "Asistencia", ruleKey: "asistencia_diaria" },
  { id: "calidad", label: "Calidad excelente", ruleKey: "calidad_excelente" },
  { id: "puntualidad", label: "Puntualidad perfecta", ruleKey: "puntualidad_perfecta" },
  { id: "cumplimiento", label: "Cumplimiento de plazo", ruleKey: "cumplimiento_plazo" },
];
const EVENT_CATEGORIES_NEG = [
  { id: "ausencia", label: "Ausencia injustificada", ruleKey: "ausencia_injustificada" },
  { id: "retraso", label: "Retraso", ruleKey: "retraso" },
  { id: "incumplimiento", label: "Incumplimiento", ruleKey: "incumplimiento" },
];
const DEFAULT_RULES = {
  asistencia_diaria: 3, calidad_excelente: 15, puntualidad_perfecta: 5,
  cumplimiento_plazo: 20, ausencia_injustificada: -20, retraso: -10, incumplimiento: -15,
};

const inputCls = "w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white transition-all";
const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5";

function getInitials(name) {
  return (name || "?").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
}
function getRankBadge(rank) {
  if (rank === 1) return { emoji: "🥇", cls: "bg-amber-100 text-amber-700 border-amber-300" };
  if (rank === 2) return { emoji: "🥈", cls: "bg-slate-100 text-slate-700 border-slate-300" };
  if (rank === 3) return { emoji: "🥉", cls: "bg-orange-100 text-orange-700 border-orange-300" };
  return { emoji: `#${rank}`, cls: "bg-gray-100 text-gray-600 border-gray-200" };
}

// ─── WORKER MODAL ─────────────────────────────────────────
function WorkerModal({ open, onClose, onSaved, worker = null, projects }) {
  const empty = { name: "", cedula: "", n_ips: "", role_title: CARGO_OPTIONS[3], project_id: "", contact_phone: "", status: "activo", hire_date: "", hourly_rate_pyg: "18000", turno_id: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [turnos, setTurnos] = useState([]);   // Fix ES (2026-09-30)

  useEffect(() => {
    if (open) {
      setForm(worker ? { ...worker, n_ips: worker.n_ips || "", hourly_rate_pyg: worker.hourly_rate_pyg ?? 18000, project_id: worker.project_id || "", turno_id: worker.turno_id || "" } : empty);
      setError("");
      axios.get(`${API}/hr/turnos`, { withCredentials: true }).then(r => setTurnos(r.data || [])).catch(() => setTurnos([]));
    }
  }, [open, worker]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim() || !form.role_title) { setError("Nombre y cargo son obligatorios"); return; }
    setSaving(true); setError("");
    try {
      const payload = { ...form, hourly_rate_pyg: Number(form.hourly_rate_pyg) || 18000, project_id: form.project_id || null };
      if (worker) await axios.put(`${API}/hr/workers/${worker.id || worker._id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/hr/workers`, payload, { withCredentials: true });
      toast.success(worker ? "Trabajador actualizado" : "Trabajador registrado");
      onSaved();
    } catch (err) { setError(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="worker-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="worker-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
          <div><h2 className="text-base font-bold text-gray-900">{worker ? "Editar Trabajador" : "Registrar Trabajador"}</h2><p className="text-xs text-gray-400 mt-0.5">Personal de CEN Ingeniería</p></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="worker-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className={labelCls}>Nombre completo *</label><input name="name" value={form.name} onChange={handleChange} placeholder="Ej: Juan Carlos Rodríguez" className={inputCls} data-testid="worker-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Cédula</label><input name="cedula" value={form.cedula} onChange={handleChange} placeholder="4.567.890" className={`${inputCls} font-mono`} data-testid="worker-cedula-input" /></div>
            <div><label className={labelCls}>N° IPS</label><input name="n_ips" value={form.n_ips} onChange={handleChange} placeholder="N° afiliación IPS" className={`${inputCls} font-mono`} data-testid="worker-ips-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Teléfono</label><input name="contact_phone" value={form.contact_phone} onChange={handleChange} placeholder="0981-123456" className={inputCls} data-testid="worker-phone-input" /></div>
            <div><label className={labelCls}>Cargo *</label>
              <select name="role_title" value={form.role_title} onChange={handleChange} className={inputCls} data-testid="worker-cargo-select">
                {CARGO_OPTIONS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Proyecto Asignado</label>
              <select name="project_id" value={form.project_id} onChange={handleChange} className={inputCls} data-testid="worker-project-select">
                <option value="">Sin asignación</option>
                {(projects || []).filter(p => p.status === "activo").map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Estado</label>
              <select name="status" value={form.status} onChange={handleChange} className={inputCls} data-testid="worker-status-select">
                {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Fecha Ingreso</label><input name="hire_date" type="date" value={form.hire_date || ""} onChange={handleChange} className={inputCls} data-testid="worker-hiredate-input" /></div>
            <div><label className={labelCls}>Jornal diario (₲ por 8 hs)</label><input name="hourly_rate_pyg" type="number" value={form.hourly_rate_pyg} onChange={handleChange} placeholder="18000" className={inputCls} data-testid="worker-rate-input" /></div>
          </div>
          {/* Fix ES (2026-09-30): turno con el que se comparan sus asistencias */}
          <div><label className={labelCls}>Turno</label>
            <select name="turno_id" value={form.turno_id || ""} onChange={handleChange} className={inputCls} data-testid="worker-turno-select">
              <option value="">Sin turno</option>
              {turnos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 text-sm font-medium hover:bg-gray-50 transition-all" data-testid="worker-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60" data-testid="worker-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {worker ? "Guardar Cambios" : "Registrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── MANUAL EVENT MODAL ───────────────────────────────────
function EventModal({ open, onClose, onSaved, worker, rules }) {
  const [form, setForm] = useState({ type: "positive", category: "calidad", points: "", description: "", date: new Date().toISOString().split("T")[0] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ type: "positive", category: "calidad", points: rules?.calidad_excelente || 15, description: "", date: new Date().toISOString().split("T")[0] });
  }, [open, rules]);

  const categories = form.type === "positive" ? EVENT_CATEGORIES_POS : EVENT_CATEGORIES_NEG;

  const handleTypeChange = t => {
    const cat = t === "positive" ? EVENT_CATEGORIES_POS[1] : EVENT_CATEGORIES_NEG[0];
    const pts = rules?.[cat.ruleKey] || (t === "positive" ? 10 : -10);
    setForm(f => ({ ...f, type: t, category: cat.id, points: Math.abs(pts) }));
  };

  const handleCatChange = catId => {
    const cats = form.type === "positive" ? EVENT_CATEGORIES_POS : EVENT_CATEGORIES_NEG;
    const cat = cats.find(c => c.id === catId);
    const pts = cat ? Math.abs(rules?.[cat.ruleKey] || 10) : 10;
    setForm(f => ({ ...f, category: catId, points: pts }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Describa el evento"); return; }
    setSaving(true);
    try {
      const pts = form.type === "penalty" ? -Math.abs(Number(form.points)) : Math.abs(Number(form.points));
      await axios.post(`${API}/hr/performance/events`, {
        worker_id: worker.id || worker._id,
        worker_name: worker.name,
        type: form.type,
        category: form.category,
        points: pts,
        description: form.description,
        date: form.date,
      }, { withCredentials: true });
      toast.success(form.type === "positive" ? "Premio registrado" : "Penalización registrada");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (!open || !worker) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="event-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="event-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div><h2 className="text-base font-bold text-gray-900">Registrar Evento</h2><p className="text-xs text-gray-400">{worker.name}</p></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="event-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className={labelCls}>Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: "positive", label: "Premio", cls: "border-emerald-400 bg-emerald-50 text-emerald-700" }, { v: "penalty", label: "Penalización", cls: "border-red-400 bg-red-50 text-red-700" }].map(opt => (
                <button key={opt.v} type="button" onClick={() => handleTypeChange(opt.v)} className={`py-3 rounded-xl border-2 font-semibold text-sm transition-all ${form.type === opt.v ? opt.cls : "border-gray-200 text-gray-500 hover:border-gray-300"}`} data-testid={`event-type-${opt.v}`}>{opt.label}</button>
              ))}
            </div>
          </div>
          <div><label className={labelCls}>Categoría</label>
            <select value={form.category} onChange={e => handleCatChange(e.target.value)} className={inputCls} data-testid="event-category-select">
              {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Puntos</label><input type="number" value={form.points} onChange={e => setForm(f => ({ ...f, points: e.target.value }))} className={inputCls} data-testid="event-points-input" /></div>
            <div><label className={labelCls}>Fecha</label><input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className={inputCls} data-testid="event-date-input" /></div>
          </div>
          <div><label className={labelCls}>Descripción *</label><input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe el motivo del evento..." className={inputCls} data-testid="event-description-input" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 text-sm font-medium hover:bg-gray-50 transition-all" data-testid="event-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className={`flex-1 ${form.type === "positive" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} active:scale-[0.98] text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60`} data-testid="event-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />}
              Registrar
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── BONUS MODAL ──────────────────────────────────────────
function BonusModal({ open, onClose, onSaved, worker, projects, bonusRate }) {
  const [pts, setPts] = useState("");
  const [projectId, setProjectId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setPts(""); setProjectId(""); } }, [open]);

  const amount = Math.round((Number(pts) || 0) * (bonusRate || 1000));
  const totalPts = worker?.total_points || 0;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!pts || Number(pts) <= 0) { toast.error("Ingrese una cantidad de puntos válida"); return; }
    if (Number(pts) > totalPts) { toast.error(`Solo tiene ${totalPts} puntos disponibles`); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/hr/workers/${worker.worker_id || worker.id}/bonus`, {
        worker_id: worker.worker_id || worker.id,
        worker_name: worker.worker_name || worker.name,
        points_to_convert: Number(pts),
        project_id: projectId || null,
      }, { withCredentials: true });
      toast.success(`Bono de ${formatPYGCompact(amount)} registrado`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (!open || !worker) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="bonus-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" data-testid="bonus-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center"><Gift size={16} className="text-amber-600" /></div><div><h2 className="text-base font-bold text-gray-900">Canjear Puntos</h2><p className="text-xs text-gray-400">{worker.worker_name || worker.name} — {totalPts} pts disponibles</p></div></div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="bonus-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className={labelCls}>Puntos a canjear</label><input type="number" value={pts} onChange={e => setPts(e.target.value)} max={totalPts} placeholder={`Máx: ${totalPts}`} className={`${inputCls} text-base`} data-testid="bonus-points-input" /></div>
          {amount > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center"><p className="text-xs text-amber-600 font-medium">Bono a recibir</p><p className="text-2xl font-bold text-amber-700">{formatPYGCompact(amount)}</p><p className="text-xs text-amber-500">Tasa: {formatPYGCompact(bonusRate)} / punto</p></div>}
          <div><label className={labelCls}>Cargar a proyecto</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className={inputCls} data-testid="bonus-project-select">
              <option value="">Sin proyecto específico</option>
              {(projects || []).filter(p => p.status === "activo").map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-3 font-medium hover:bg-gray-50 transition-all" data-testid="bonus-modal-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 active:scale-[0.98] text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="bonus-modal-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
              Canjear
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ─── SCORE CARD MODAL (MOBILE WORKER VIEW) ────────────────
function ScoreCardModal({ open, onClose, worker, events }) {
  if (!open || !worker) return null;
  const recent = (events || []).filter(e => e.worker_id === (worker.worker_id || worker.id)).slice(0, 5);
  const pts = worker.total_points || 0;
  const badge = getRankBadge(worker.rank || 99);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="scorecard-modal-overlay">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md" data-testid="scorecard-modal">
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-[#1a3f6a] to-[#2E6DA4] rounded-t-3xl sm:rounded-t-2xl p-6 text-white text-center relative">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors" data-testid="scorecard-close"><X size={18} /></button>
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3 text-2xl font-bold">
            {getInitials(worker.worker_name || worker.name)}
          </div>
          <h2 className="text-lg font-bold">{worker.worker_name || worker.name}</h2>
          <p className="text-blue-200 text-sm">{worker.role_title || ""}</p>
          {worker.project_name && <p className="text-blue-300 text-xs mt-0.5">{worker.project_name}</p>}
        </div>

        <div className="p-6 space-y-4">
          {/* Points big display */}
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Puntos Acumulados</p>
              <p className={`text-5xl font-black mt-1 ${pts >= 0 ? "text-[#2E6DA4]" : "text-red-500"}`}>{pts}</p>
            </div>
            <div className="text-center flex-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Posición</p>
              <div className={`inline-flex items-center gap-1 mt-1 px-4 py-2 rounded-xl border-2 font-bold text-lg ${badge.cls}`}>
                <span>{badge.emoji}</span>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-emerald-700">{worker.positive_events || 0}</p>
              <p className="text-xs text-emerald-600 font-medium">Premios</p>
            </div>
            <div className="bg-red-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-red-700">{worker.penalty_events || 0}</p>
              <p className="text-xs text-red-600 font-medium">Penalizaciones</p>
            </div>
          </div>

          {/* Recent events */}
          {recent.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Últimos Eventos</p>
              <div className="space-y-2">
                {recent.map(e => (
                  <div key={e.id} className="flex items-center gap-2.5 py-1.5">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${e.points > 0 ? "bg-emerald-100" : "bg-red-100"}`}>
                      {e.points > 0 ? <TrendingUp size={12} className="text-emerald-600" /> : <TrendingDown size={12} className="text-red-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700 font-medium truncate">{e.description}</p>
                      <p className="text-xs text-gray-400">{e.date}</p>
                    </div>
                    <span className={`text-xs font-bold flex-shrink-0 ${e.points > 0 ? "text-emerald-600" : "text-red-600"}`}>{e.points > 0 ? "+" : ""}{e.points}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <button onClick={onClose} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-3.5 font-semibold text-sm transition-all active:scale-[0.98]" data-testid="scorecard-close-btn">Cerrar</button>
        </div>
      </div>
    </div>
  , document.body);
}

// ─── PERSONAL TAB ─────────────────────────────────────────
function PersonalTab({ workers, loading, projects, onNew, onEdit, onRefresh }) {
  const [search, setSearch] = useState("");
  const [turnosOpen, setTurnosOpen] = useState(false);   // Fix ES (2026-09-30)
  const [projectFilter, setProjectFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("activo");

  const filtered = workers.filter(w => {
    const matchSearch = !search || w.name?.toLowerCase().includes(search.toLowerCase()) || w.role_title?.toLowerCase().includes(search.toLowerCase());
    const matchProject = projectFilter === "todos" || w.project_id === projectFilter;
    const matchStatus = statusFilter === "todos" || w.status === statusFilter;
    return matchSearch && matchProject && matchStatus;
  });

  // Fix EI (2026-09-27): los remanentes de la base (inactivos) se pueden eliminar.
  const handleEliminar = async (w) => {
    if (!window.confirm(`¿Eliminar a ${w.name}? Deja de aparecer en las listas; sus registros no se borran.`)) return;
    try {
      await axios.delete(`${API}/hr/workers/${w.id || w._id}/papelera`, { withCredentials: true });
      toast.success("Trabajador eliminado");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar"); }
  };

  const handleDeactivate = async (w) => {
    if (!window.confirm(`¿Desactivar a ${w.name}?`)) return;
    try {
      await axios.delete(`${API}/hr/workers/${w.id || w._id}`, { withCredentials: true });
      toast.success("Trabajador desactivado");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="space-y-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="personal-tab">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o cargo..." className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="workers-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["todos", "activo", "licencia", "inactivo"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-2 rounded-xl text-xs font-semibold capitalize transition-all border whitespace-nowrap ${statusFilter === s ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-300 hover:border-[#2E6DA4]/40"}`} data-testid={`status-filter-${s}`}>
              {s === "todos" ? "Todos" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        {/* Fix ES (2026-09-30): parámetros con los que se comparan las asistencias */}
        <button onClick={() => setTurnosOpen(true)} className="flex items-center justify-center gap-2 bg-white border border-[#2E6DA4]/40 text-[#2E6DA4] hover:bg-[#EBF2F9] px-4 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap" data-testid="turnos-btn">
          <Clock size={16} />Turnos
        </button>
        <button onClick={onNew} className="flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm whitespace-nowrap" data-testid="new-worker-btn">
          <Plus size={16} />Registrar Personal
        </button>
      </div>
      <TurnosModal open={turnosOpen} onClose={() => setTurnosOpen(false)} workers={workers} onChanged={onRefresh} />

      {/* Project filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button onClick={() => setProjectFilter("todos")} className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${projectFilter === "todos" ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-200"}`} data-testid="project-filter-todos">Todos los proyectos</button>
        <button onClick={() => setProjectFilter(null)} className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${projectFilter === null ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-200"}`} data-testid="project-filter-none">Sin proyecto</button>
        {(projects || []).filter(p => p.status === "activo").map(p => (
          <button key={p._id || p.id} onClick={() => setProjectFilter(p._id || p.id)} className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${projectFilter === (p._id || p.id) ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-200"}`} data-testid={`project-filter-${p._id || p.id}`}>
            {p.name.split(" ").slice(0, 3).join(" ")}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="workers-list">
        {filtered.length === 0 ? (
          <div className="p-8 text-center"><Users size={32} className="text-gray-300 mx-auto mb-2" /><p className="text-gray-500 text-sm">No se encontraron trabajadores</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Trabajador</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Cargo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">N° IPS</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Proyecto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Contacto</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Jornal diario</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(w => (
                  <tr key={w.id || w._id} className="hover:bg-gray-50/60 transition-colors" data-testid="worker-row">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-[#2E6DA4]/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-[#2E6DA4]">{getInitials(w.name)}</div>
                        <div><p className="font-semibold text-gray-900 text-xs">{w.name}</p>{w.hire_date && <p className="text-xs text-gray-400">Desde {w.hire_date?.split("-")[0]}</p>}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 hidden sm:table-cell">
                      {w.role_title}
                      {w.turno_nombre && <span className="block text-[10px] text-violet-600 font-semibold" data-testid={`worker-turno-${w.id || w._id}`}>Turno: {w.turno_nombre}</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono hidden lg:table-cell">{w.n_ips || "—"}</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {w.project_name ? <span className="text-xs bg-[#EBF2F9] text-[#2E6DA4] px-2 py-1 rounded-lg font-medium">{w.project_name.split(" ").slice(0, 3).join(" ")}</span> : <span className="text-xs text-gray-400">Sin proyecto</span>}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-xs text-gray-500">{w.contact_phone || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-right text-gray-700 font-mono tabular-nums">{w.hourly_rate_pyg ? `₲ ${Number(w.hourly_rate_pyg).toLocaleString("es-PY")}` : "—"}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-semibold capitalize ${STATUS_BADGE[w.status] || "bg-gray-100 text-gray-600"}`}>{w.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => onEdit(w)} className="p-1.5 text-gray-400 hover:text-[#2E6DA4] hover:bg-[#EBF2F9] rounded-lg transition-all" data-testid={`edit-worker-${w.id || w._id}`} title="Editar"><FileText size={14} /></button>
                        {w.status === "activo" && <button onClick={() => handleDeactivate(w)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" data-testid={`deactivate-worker-${w.id || w._id}`} title="Desactivar"><Trash2 size={14} /></button>}
                        {w.status === "inactivo" && <button onClick={() => handleEliminar(w)} className="px-2 py-1 text-[11px] font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-all" data-testid={`eliminar-worker-${w.id || w._id}`} title="Eliminar">Eliminar</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DESEMPEÑO TAB ────────────────────────────────────────
function DesempenoTab({ projects, rules, bonusRate, onRefresh }) {
  const [ranking, setRanking] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [eventModal, setEventModal] = useState({ open: false, worker: null });
  const [bonusModal, setBonusModal] = useState({ open: false, worker: null });
  const [scoreCard, setScoreCard] = useState({ open: false, worker: null });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if (projectFilter) params.push(`project_id=${projectFilter}`);
      if (month) params.push(`month=${month}`);
      const query = params.length ? `?${params.join("&")}` : "";
      const [rankRes, evtRes] = await Promise.all([
        axios.get(`${API}/hr/performance/ranking${query}`, { withCredentials: true }),
        axios.get(`${API}/hr/performance/events?limit=100`, { withCredentials: true }),
      ]);
      setRanking(rankRes.data);
      setEvents(evtRes.data);
    } catch (e) { toast.error("Error al cargar ranking"); }
    finally { setLoading(false); }
  }, [projectFilter, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const maxPts = Math.max(...ranking.map(r => r.total_points), 1);

  const onEventSaved = () => { setEventModal({ open: false, worker: null }); fetchData(); onRefresh(); };
  const onBonusSaved = () => { setBonusModal({ open: false, worker: null }); fetchData(); onRefresh(); };

  if (loading) return <div className="space-y-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="desempeno-tab">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="ranking-month-filter" />
        <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] flex-1" data-testid="ranking-project-filter">
          <option value="">Todos los proyectos</option>
          {(projects || []).map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Ranking list */}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="ranking-list">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ranking de Desempeño — {month}</p>
          <span className="text-xs text-gray-400">{ranking.length} trabajadores</span>
        </div>
        {ranking.length === 0 ? (
          <div className="p-8 text-center"><Star size={32} className="text-gray-300 mx-auto mb-2" /><p className="text-gray-500 text-sm">Sin datos de desempeño para este período</p></div>
        ) : ranking.map((r, idx) => {
          const badge = getRankBadge(r.rank);
          const pct = Math.round((Math.max(r.total_points, 0) / Math.max(maxPts, 1)) * 100);
          return (
            <div key={r.worker_id} className={`px-4 py-3 flex items-center gap-3 hover:bg-gray-50/60 transition-colors border-b border-gray-100 last:border-0 ${idx < 3 ? "bg-gradient-to-r from-transparent to-transparent hover:from-gray-50" : ""}`} data-testid="ranking-row">
              {/* Rank badge */}
              <div className={`w-9 h-9 rounded-xl border-2 flex items-center justify-center font-bold text-xs flex-shrink-0 ${badge.cls}`}>
                {typeof badge.emoji === "string" ? badge.emoji : badge.emoji}
              </div>
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-[#2E6DA4]/10 flex items-center justify-center font-bold text-xs text-[#2E6DA4] flex-shrink-0">{getInitials(r.worker_name)}</div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-gray-900">{r.worker_name}</p>
                  {r.role_title && <span className="text-xs text-gray-500 hidden sm:block">{r.role_title}</span>}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-[140px]">
                    <div className={`h-1.5 rounded-full transition-all duration-700 ${r.total_points >= 0 ? "bg-[#2E6DA4]" : "bg-red-500"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-gray-400"><span className="text-emerald-600 font-medium">+{r.positive_events}</span> / <span className="text-red-500 font-medium">-{r.penalty_events}</span></span>
                </div>
              </div>
              {/* Points */}
              <div className="text-right flex-shrink-0">
                <p className={`text-lg font-black tabular-nums ${r.total_points >= 0 ? "text-[#2E6DA4]" : "text-red-500"}`}>{r.total_points > 0 ? "+" : ""}{r.total_points}</p>
                <p className="text-xs text-gray-400">pts</p>
              </div>
              {/* Actions */}
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => setScoreCard({ open: true, worker: r })} className="p-1.5 text-gray-400 hover:text-[#2E6DA4] hover:bg-[#EBF2F9] rounded-lg transition-all" data-testid={`view-score-${r.worker_id}`} title="Ver perfil"><User size={14} /></button>
                <button onClick={() => setEventModal({ open: true, worker: { id: r.worker_id, name: r.worker_name } })} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all" data-testid={`add-event-${r.worker_id}`} title="Registrar evento"><Star size={14} /></button>
                <button onClick={() => setBonusModal({ open: true, worker: r })} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all" data-testid={`bonus-btn-${r.worker_id}`} title="Canjear puntos"><Gift size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>

      <EventModal open={eventModal.open} onClose={() => setEventModal({ open: false, worker: null })} onSaved={onEventSaved} worker={eventModal.worker} rules={rules} />
      <BonusModal open={bonusModal.open} onClose={() => setBonusModal({ open: false, worker: null })} onSaved={onBonusSaved} worker={bonusModal.worker} projects={projects} bonusRate={bonusRate} />
      <ScoreCardModal open={scoreCard.open} onClose={() => setScoreCard({ open: false, worker: null })} worker={scoreCard.worker} events={events} />
    </div>
  );
}

// ─── LIQUIDACIÓN TAB ──────────────────────────────────────
function LiquidacionTab({ workers, projects }) {
  // Fix FC (2026-10-06): "El proceso sería: 1-Carga de asistencias en Parte Diario Adm,
  // 2-Vinculación de asistencias a Ptos en Parte Diario Obra, 3-Se cargan los adelantos si
  // los hubiere en Adelantos, 4-En Liquidación se genera la Orden de Pago de Liquidación que
  // va a Tesorería" (CEN). Liquida los días con Parte Obra validado y descuenta los adelantos
  // pagados del período.
  const today = new Date();
  const [periodFrom, setPeriodFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0]);
  const [periodTo, setPeriodTo] = useState(today.toISOString().split("T")[0]);
  const [projectId, setProjectId] = useState("");
  // Fix GB (2026-10-14): "Este tipo de filtro (turno, obra, empleado, fechas) si podemos tener también en
  // RRHH, Liquidación" · "Agregar también un filtro múltiple por cargos (Ing Residente, Proyectista,
  // Jefe de Compras, etc)" (CEN).
  const turnos = useTurnos();
  const [turnoId, setTurnoId] = useState("");
  const [cargos, setCargos] = useState([]);
  const [elegidos, setElegidos] = useState([]);
  const [data, setData] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Fix FJ (2026-10-08): fechas validadas en el Parte Diario Adm. que todavía no se imputaron
  // en el Parte Diario Obra (con alguna, es un borrador y no se genera la OP).
  const [sinImputar, setSinImputar] = useState([]);
  // Fix FY (2026-10-14): trabajadores con el jornal sin cargar (vacío o 18.000 por defecto): no se liquidan.
  const [sinJornal, setSinJornal] = useState([]);
  // Fix GD (2026-10-14): "Para la liquidación del CEN Operativo hace falta una matriz de prorrateo" (CEN).
  // Con el interruptor, el costo del personal elegido se reparte entre obras con un % que valida la Directiva.
  const { user } = useAuth();
  const puedeValidarPr = roleAllowed(user?.role, PRORRATEO_ROLES);
  const [prorrateoOn, setProrrateoOn] = useState(false);
  const mp = useMatrizProrrateo();
  const ingresos = useIngresosObras(periodFrom, periodTo, prorrateoOn);

  const activeProjects = (projects || []).filter(p => p.status === "activo");
  const cargosDisponibles = [...new Set((workers || []).map(w => (w.role_title || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"));
  const visibles = filtrarEmpleados(workers, turnoId, projectId).filter(w => !cargos.length || cargos.includes((w.role_title || "").trim()));
  // Sin ningún filtro, todos; con filtros, los que los cumplen (y, si se eligieron empleados, solo esos).
  const workerIds = () => {
    if (!turnoId && !projectId && !cargos.length && !elegidos.length) return undefined;
    const ids = visibles.map(w => w.id || w._id);
    return elegidos.length ? elegidos.filter(id => ids.includes(id)) : ids;
  };

  const fetchLiquidacion = async () => {
    if (!periodFrom || !periodTo) { toast.error("Definí el período"); return; }
    const ids = workerIds();
    if ((ids && !ids.length) || (prorrateoOn && !ids)) { setData([]); setDetalle(null); setSinImputar([]); setSinJornal([]); return; }   // Fix GB · Fix GD: el prorrateo pide elegir personal
    setLoading(true);
    try {
      const res = await axios.post(`${API}/hr/parte-diario/generate-ops`,
        { date_from: periodFrom, date_to: periodTo, worker_ids: ids, preview: true, prorrateo_modo: prorrateoOn }, { withCredentials: true });
      mp.setValidada(false);   // Fix GD: si cambian los jornales, hay que validar de nuevo
      setData(res.data.filas || []);
      setDetalle(res.data.detalle || null);
      setSinImputar(res.data.fechas_sin_imputar || []);   // Fix FJ
      setSinJornal(res.data.sin_jornal || []);   // Fix FY
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al calcular la liquidación");
    } finally { setLoading(false); }
  };

  // Fix GD (2026-10-14): recalcula también al activar o quitar el prorrateo.
  useEffect(() => { fetchLiquidacion(); }, [prorrateoOn]);   // eslint-disable-line react-hooks/exhaustive-deps

  const generateOPs = async () => {
    if (!data.length) { toast.error("No hay días para liquidar"); return; }
    if (prorrateoOn && !mp.validada) { toast.error("Validá la distribución entre obras antes de generar las OP"); return; }   // Fix GD
    if (!window.confirm(`¿Generar las Órdenes de Pago de Liquidación de ${data.length} trabajador(es)? Total: ${formatPYG(totalNeto)}`
      + (prorrateoOn ? "\nEl jornal se imputa a las obras cuando Tesorería apruebe cada OP." : ""))) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/hr/parte-diario/generate-ops`,
        { date_from: periodFrom, date_to: periodTo, worker_ids: workerIds(),
          ...(prorrateoOn ? { prorrateo_modo: true, prorrateo_validado: true,
            prorrateo: data.map(r => ({ worker_id: r.worker_id, partes: mp.partesDe(r.worker_id) })) } : {}) }, { withCredentials: true });   // Fix GD
      toast.success(`${res.data.count || 0} OP de liquidación generada(s) — van a Tesorería · Total ${formatPYG(res.data.total || 0)}`);
      if ((res.data.sin_saldo || []).length) toast.warning(`Sin OP (los adelantos cubren lo liquidado): ${res.data.sin_saldo.join(", ")}`, { duration: 12000 });
      if ((res.data.sin_distribucion || []).length) toast.warning(`Sin OP (distribución entre obras inválida): ${res.data.sin_distribucion.join(", ")}`, { duration: 12000 });   // Fix GD
      fetchLiquidacion();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar las OP");
    } finally { setGenerating(false); }
  };

  const horasExtra = (d) => (d.horas_extra || 0) + (d.horas_extra_nocturna || 0) + (d.horas_extra_feriado || 0);   // Fix FY · Fix GA
  const totalBruto = data.reduce((s, d) => s + (d.bruto || 0), 0);
  const totalAdelantos = data.reduce((s, d) => s + (d.adelantos || 0), 0);
  const totalDescuentos = data.reduce((s, d) => s + (d.descuentos || 0), 0);   // Fix FJ
  const esBorrador = sinImputar.length > 0;
  const fechaCorta = (f) => (f ? `${f.slice(8, 10)}/${f.slice(5, 7)}` : "");
  // Fix FJ (2026-10-08): "Estos borradores pueden visualizarse e imprimirse" (CEN).
  const imprimir = () => {
    exportarTablaPdf({
      titulo: esBorrador ? "Liquidación — BORRADOR (faltan imputaciones del Parte Diario Obra)" : "Liquidación",
      filtros: [`Del ${fechaReporte(periodFrom)} al ${fechaReporte(periodTo)}`,
        turnoId ? `Turno: ${turnoId === "__sin__" ? "Sin turno asignado" : (turnos.find(t => t.id === turnoId) || {}).nombre || ""}` : "",   // Fix GB
        projectId ? `Obra: ${(activeProjects.find(p => (p._id || p.id) === projectId) || {}).name || ""}` : "",
        cargos.length ? `Cargo: ${cargos.join(", ")}` : "",
        elegidos.length ? `Empleados: ${elegidos.length}` : "",
        esBorrador ? `Sin imputar: ${sinImputar.map(fechaCorta).join(", ")}` : ""].filter(Boolean).join(" · "),
      columnas: [{ l: "Trabajador" }, { l: "Cargo" }, { l: "Días", num: true }, { l: "Horas" }, { l: "Hs normales" }, { l: "Hs noct." }, { l: "Hs extra" }, { l: "Hs domingo" }, { l: "Tarifa/h", num: true },   // Fix FY: con decimales · Fix GA
        { l: "Jornales", num: true }, { l: "Adelantos", num: true }, { l: "Descuentos", num: true }, { l: "A pagar", num: true }, { l: "Fechas sin imputar" }],
      filas: data.map(r => [r.worker_name, r.role_title || "", r.dias, Number(r.horas || 0).toFixed(2), Number(r.horas_normales || 0).toFixed(2), Number(r.horas_nocturnas || 0).toFixed(2), horasExtra(r).toFixed(2), Number(r.horas_domingo || 0).toFixed(2), r.tarifa, r.bruto, r.adelantos || 0,
        r.descuentos || 0, r.neto, (r.fechas_sin_imputar || []).map(fechaCorta).join(", ")]),
      totales: ["TOTALES", "", "", totalHoras.toFixed(2), totalNormales.toFixed(2), totalNoct.toFixed(2), totalExtra.toFixed(2), totalDomingo.toFixed(2), "", totalBruto, totalAdelantos, totalDescuentos, totalNeto, ""],
      archivo: `Liquidacion_${esBorrador ? "BORRADOR_" : ""}${periodFrom}_${periodTo}.pdf`,
    });
  };
  const totalNeto = data.reduce((s, d) => s + Math.max(d.neto || 0, 0), 0);
  const totalHoras = data.reduce((s, d) => s + (d.horas || 0), 0);
  const totalNormales = data.reduce((s, d) => s + (d.horas_normales || 0), 0);   // Fix FY
  const totalExtra = data.reduce((s, d) => s + horasExtra(d), 0);   // Fix FY
  const totalNoct = data.reduce((s, d) => s + (d.horas_nocturnas || 0), 0);   // Fix GA
  const totalDomingo = data.reduce((s, d) => s + (d.horas_domingo || 0), 0);
  const motivoVacio = () => {
    if (prorrateoOn && !workerIds()) return "Para liquidar al CEN Operativo elegí el cargo o los empleados: el prorrateo no se aplica a todo el personal.";   // Fix GD
    const d = detalle || {};
    if (!d.dias) return "No hay días cargados en ese período. Primero cargá y validá el Parte Diario Adm. y después el Parte Diario Obra.";
    const partes = [];
    if (d.sin_validar_admin) partes.push(`${d.sin_validar_admin} sin validar en el Parte Diario Adm.`);
    if (d.sin_validar_obra) partes.push(`${d.sin_validar_obra} sin validar en el Parte Diario Obra`);
    if (d.ya_liquidados) partes.push(`${d.ya_liquidados} ya liquidado(s)`);
    return `No hay días para liquidar. De los ${d.dias} día(s) del período: ${partes.join(", ") || "ninguno se puede liquidar"}.`;
  };

  return (
    <div className="space-y-4" data-testid="liquidacion-tab">
      <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div><label className={labelCls}>Turno</label>
            <SelectTurno turnos={turnos} value={turnoId} onChange={setTurnoId} className={inputCls} testid="liq-turno-select" />
          </div>
          <div><label className={labelCls}>Obra / Proyecto</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className={inputCls} data-testid="liq-project-select">
              <option value="">Todas</option>
              {activeProjects.map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Cargo</label>
            <SelectorCargos cargos={cargosDisponibles} value={cargos} onChange={setCargos} testid="liq-cargo-select" />
          </div>
          <div><label className={labelCls}>Nombre (empleado)</label>
            <SelectorEmpleados workers={visibles} value={elegidos} onChange={setElegidos}
              testid="liq-worker-select" placeholder="Todos los empleados" />
          </div>
          <div><label className={labelCls}>Fecha inicio</label><input type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)} className={inputCls} data-testid="liq-from-input" /></div>
          <div><label className={labelCls}>Fecha fin</label><input type="date" value={periodTo} onChange={e => setPeriodTo(e.target.value)} className={inputCls} data-testid="liq-to-input" /></div>
          <div className="flex items-end sm:col-span-2">
            <button onClick={fetchLiquidacion} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-all" data-testid="liq-calc-btn">
              <RefreshCw size={14} /> Calcular
            </button>
          </div>
        </div>
        {/* Fix GD (2026-10-14): liquidación del CEN Operativo, con prorrateo entre obras. */}
        <label className="flex items-start gap-2 mt-3 text-xs text-gray-700 cursor-pointer" data-testid="liq-prorrateo-label">
          <input type="checkbox" checked={prorrateoOn} onChange={e => setProrrateoOn(e.target.checked)} className="mt-0.5" data-testid="liq-prorrateo-toggle" />
          <span><b>Liquidar como CEN Operativo</b> — repartir el costo del personal elegido entre varias obras según un % por trabajador (elegí el cargo o los empleados).</span>
        </label>
        <p className="text-[11px] text-gray-400 mt-2">Calcula con el Parte Diario Adm. validado (borrador mientras falten imputaciones del Parte Diario Obra), descuenta los adelantos pagados del período y una cuota de cada descuento. La Orden de Pago se genera con todas las fechas imputadas y va a Tesorería. Valor hora = jornal ÷ 8; cada tipo de hora (nocturna, extra, extra nocturna, domingo y feriado) se paga con el factor y el redondeo que se cargan en el turno (RRHH › Turnos).</p>
      </div>

      {sinJornal.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-xs text-red-800" data-testid="liq-sin-jornal-aviso">
          <p className="font-bold">Sin jornal cargado (no se liquidan): {sinJornal.join(", ")}</p>
          <p className="mt-0.5">Cargá el jornal diario de cada uno en Personal y volvé a calcular.</p>
        </div>
      )}

      {/* Fix FJ (2026-10-08): "OBS1: Que se visualice que fechas están sin imputación con una leyenda que lo indique" (CEN). */}
      {esBorrador && data.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800" data-testid="liq-borrador-aviso">
          <p className="font-bold">Borrador: {sinImputar.length} fecha(s) todavía sin imputar en el Parte Diario Obra</p>
          <p className="mt-0.5">{sinImputar.map(fechaCorta).join(", ")}. La Orden de Pago se genera cuando estén todas imputadas.</p>
        </div>
      )}

      {prorrateoOn && data.length > 0 && <MatrizProrrateo m={mp} filas={data} ingresos={ingresos} puedeValidar={puedeValidarPr} />}   {/* Fix GD */}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-4">
          <p className="text-xs font-semibold text-emerald-700 uppercase">Trabajadores</p>
          <p className="text-2xl font-bold text-emerald-700 mt-1">{data.length}</p>
          <p className="text-[11px] text-emerald-700/70 tabular-nums">{totalHoras.toFixed(2)}h</p>
        </div>
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
          <p className="text-xs font-semibold text-blue-700 uppercase">Jornales</p>
          <p className="text-xl font-bold text-blue-700 mt-1 tabular-nums">{formatPYG(totalBruto)}</p>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-100 p-4">
          <p className="text-xs font-semibold text-red-700 uppercase">Adelantos</p>
          <p className="text-xl font-bold text-red-700 mt-1 tabular-nums" data-testid="liq-total-adelantos">− {formatPYG(totalAdelantos)}</p>
        </div>
        <div className="bg-rose-50 rounded-xl border border-rose-100 p-4">
          <p className="text-xs font-semibold text-rose-700 uppercase">Descuentos</p>
          <p className="text-xl font-bold text-rose-700 mt-1 tabular-nums" data-testid="liq-total-descuentos">− {formatPYG(totalDescuentos)}</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
          <p className="text-xs font-semibold text-amber-700 uppercase">A pagar</p>
          <p className="text-xl font-bold text-amber-700 mt-1 tabular-nums" data-testid="liq-total-neto">{formatPYG(totalNeto)}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="liq-table">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin text-gray-300" /></div>
        ) : data.length === 0 ? (
          <div className="p-8 text-center"><DollarSign size={32} className="mx-auto text-gray-300 mb-2" /><p className="text-gray-500 text-sm" data-testid="liq-vacio">{motivoVacio()}</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-100 bg-gray-50/80">
                {["Trabajador", "Cargo", "Días", "Horas", "Hs normales", "Hs noct.", "Hs extra", "Hs domingo", "Tarifa/h", "Jornales", "Adelantos", "Descuentos", "A pagar"].map((h, i) => (   /* Fix FJ */
                  // Fix GI (2026-10-15): "en ambos partes diarios y liquidación … congelar las columnas de empleado y fecha" (CEN).
                  // La Liquidación no tiene columna de fecha: queda fija la del trabajador.
                  <th key={h} className={`px-4 py-3 ${i < 2 ? "text-left" : "text-right"} text-xs font-semibold text-gray-500 uppercase tracking-wide${i === 0 ? " sticky left-0 z-20 bg-gray-50 min-w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.18)]" : ""}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.map((row, i) => (
                  <tr key={row.worker_id || i} className="group hover:bg-gray-50/60" data-testid={`liq-row-${i}`}>
                    <td className="px-4 py-3 text-xs font-semibold text-gray-900 sticky left-0 z-10 bg-white group-hover:bg-gray-50 min-w-[220px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.18)]">{row.worker_name}
                      {(row.fechas_sin_imputar || []).length > 0 && (
                        <p className="text-[10px] font-semibold text-amber-700 mt-0.5" data-testid={`liq-sin-imputar-${i}`}>Sin imputar: {row.fechas_sin_imputar.map(fechaCorta).join(", ")}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{row.role_title || "—"}</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums">{row.dias}</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums font-semibold text-[#2E6DA4]">{Number(row.horas || 0).toFixed(2)}h</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums" data-testid={`liq-normales-${i}`}>{Number(row.horas_normales || 0).toFixed(2)}h</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums" data-testid={`liq-noct-${i}`}>{row.horas_nocturnas ? `${Number(row.horas_nocturnas).toFixed(2)}h` : "—"}</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums text-violet-700" data-testid={`liq-extra-${i}`}
                      title={`Hora extra: ${Number(row.horas_extra || 0).toFixed(2)}h · extra nocturna: ${Number(row.horas_extra_nocturna || 0).toFixed(2)}h · feriado: ${Number(row.horas_extra_feriado || 0).toFixed(2)}h`}>
                      {horasExtra(row) ? `${horasExtra(row).toFixed(2)}h` : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums" data-testid={`liq-domingo-${i}`}>{row.horas_domingo ? `${Number(row.horas_domingo).toFixed(2)}h` : "—"}</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums" data-testid={`liq-tarifa-${i}`}
                      title={row.jornal ? `Jornal ${formatPYG(row.jornal)} ÷ 8` : ""}>{formatPYG(row.tarifa || 0)}</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums">{formatPYG(row.bruto || 0)}</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums text-red-600">{row.adelantos ? `− ${formatPYG(row.adelantos)}` : "—"}</td>
                    <td className="px-4 py-3 text-xs text-right tabular-nums text-rose-600" data-testid={`liq-descuentos-${i}`}
                      title={(row.detalle_descuentos || []).map(d => `${d.descripcion}: cuota ${d.cuota} de ${d.de}`).join(" · ")}>
                      {row.descuentos ? `− ${formatPYG(row.descuentos)}` : "—"}
                      {(row.detalle_descuentos || []).map((d, k) => <p key={k} className="text-[10px] text-gray-400">cuota {d.cuota}/{d.de}</p>)}
                    </td>
                    <td className={`px-4 py-3 text-xs text-right tabular-nums font-bold ${row.neto > 0 ? "text-emerald-700" : "text-red-600"}`}>{formatPYG(row.neto || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Fix FJ (2026-10-08): el borrador se imprime; la OP, solo con todo imputado. */}
          <button onClick={imprimir} className="sm:w-64 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl py-4 font-bold text-sm flex items-center justify-center gap-2" data-testid="liq-imprimir-btn">
            <FileText size={18} /> {esBorrador ? "Imprimir borrador" : "Imprimir"}
          </button>
          <button onClick={generateOPs} disabled={generating || esBorrador || (prorrateoOn && !mp.validada)} title={esBorrador ? "Faltan imputaciones del Parte Diario Obra" : ""}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl py-4 font-bold text-base flex items-center justify-center gap-3 transition-all shadow-sm" data-testid="liq-generate-ops-btn">
            {generating ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
            {generating ? "Generando..." : esBorrador ? `Faltan imputar ${sinImputar.length} fecha(s) en el Parte Diario Obra` : prorrateoOn && !mp.validada ? "Validá la distribución entre obras para generar las OP" : `Generar OP de Liquidación (${formatPYG(totalNeto)})`}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── CONFIG HR TAB ────────────────────────────────────────
function ConfigHRTab({ rules: initialRules, bonusRate: initialRate, onSaved }) {
  const [rules, setRules] = useState(initialRules || DEFAULT_RULES);
  const [bonusRate, setBonusRate] = useState(initialRate || 1000);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (initialRules) setRules(initialRules); if (initialRate) setBonusRate(initialRate); }, [initialRules, initialRate]);

  const ruleLabels = {
    asistencia_diaria: "Asistencia diaria (+pts)", calidad_excelente: "Calidad excelente (+pts)",
    puntualidad_perfecta: "Puntualidad perfecta (+pts)", cumplimiento_plazo: "Cumplimiento de plazo (+pts)",
    ausencia_injustificada: "Ausencia injustificada (pts)", retraso: "Retraso (pts)", incumplimiento: "Incumplimiento (pts)",
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/hr/settings`, { rules, bonus_conversion_rate_pyg: Number(bonusRate) }, { withCredentials: true });
      toast.success("Configuración de RRHH guardada");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="config-hr-tab">
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/80"><p className="text-sm font-bold text-gray-800">Reglas de Puntaje Automático</p><p className="text-xs text-gray-500 mt-0.5">El sistema aplica estos puntos automáticamente según los eventos registrados</p></div>
        <div className="p-5 space-y-3">
          {/* Positive rules */}
          <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Premios</p>
          {["asistencia_diaria", "calidad_excelente", "puntualidad_perfecta", "cumplimiento_plazo"].map(key => (
            <div key={key} className="flex items-center justify-between gap-4" data-testid={`rule-${key}`}>
              <label className="text-sm text-gray-700 flex-1">{ruleLabels[key]}</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-emerald-600 font-semibold">+</span>
                <input type="number" value={rules[key] || 0} onChange={e => setRules(r => ({ ...r, [key]: Number(e.target.value) }))} className="w-20 border border-gray-300 rounded-xl px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" min={0} data-testid={`rule-input-${key}`} />
              </div>
            </div>
          ))}
          {/* Penalty rules */}
          <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mt-4 pt-4 border-t border-gray-100">Penalizaciones</p>
          {["ausencia_injustificada", "retraso", "incumplimiento"].map(key => (
            <div key={key} className="flex items-center justify-between gap-4" data-testid={`rule-${key}`}>
              <label className="text-sm text-gray-700 flex-1">{ruleLabels[key]}</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-500 font-semibold">−</span>
                <input type="number" value={Math.abs(rules[key] || 0)} onChange={e => setRules(r => ({ ...r, [key]: -Math.abs(Number(e.target.value)) }))} className="w-20 border border-gray-300 rounded-xl px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" min={0} data-testid={`rule-input-${key}`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-5">
        <p className="text-sm font-bold text-gray-800 mb-1">Conversión de Puntos a Bonos</p>
        <p className="text-xs text-gray-500 mb-4">Valor monetario de cada punto al canjear por beneficios</p>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">1 punto =</span>
          <input type="number" value={bonusRate} onChange={e => setBonusRate(e.target.value)} className="w-36 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="bonus-rate-input" />
          <span className="text-sm text-gray-500 font-medium">₲</span>
        </div>
        {bonusRate > 0 && <p className="text-xs text-gray-400 mt-2">Ejemplo: 100 puntos → {formatPYGCompact(bonusRate * 100)}</p>}
      </div>

      <AprobadoresAdelantosCard />   {/* Fix FJ (2026-10-08) */}

      <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] active:scale-[0.98] text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all shadow-sm disabled:opacity-60" data-testid="save-hr-config-btn">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
        Guardar Configuración
      </button>
    </div>
  );
}

// ─── ADELANTOS / DESCUENTOS ───────────────────────────────
// Fix FJ (2026-10-08): pasó a hr/AdelantosDescuentosTab.jsx (adelantos y descuentos, con
// aprobación según Config HR y Pedido de OP a Tesorería).

// ─── MAIN HR COMPONENT ────────────────────────────────────
export default function HR() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("personal");
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [hrSettings, setHrSettings] = useState({ rules: DEFAULT_RULES, bonus_conversion_rate_pyg: 1000 });
  const [loading, setLoading] = useState(true);
  const [workerModal, setWorkerModal] = useState({ open: false, item: null });

  const fetchAll = useCallback(async () => {
    try {
      const [wRes, pRes, sRes, settingsRes] = await Promise.all([
        axios.get(`${API}/hr/workers`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/hr/stats`, { withCredentials: true }),
        axios.get(`${API}/hr/settings`, { withCredentials: true }),
      ]);
      setWorkers(wRes.data);
      setProjects(pRes.data);
      setStats(sRes.data);
      setHrSettings(settingsRes.data);
    } catch (e) { toast.error("Error al cargar módulo RRHH"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refresh = useCallback(() => { setLoading(true); fetchAll(); }, [fetchAll]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-h-full" data-testid="hr-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Recursos Humanos</h1>
          <p className="text-sm text-gray-500 mt-0.5">Personal, parte diario, desempeño y bonos</p>
        </div>
        <button onClick={refresh} className="p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all" data-testid="hr-refresh-btn"><RefreshCw size={18} /></button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="hr-stats">
        {[
          { label: "Total Personal", value: stats?.total ?? "—", icon: Users, color: "text-[#2E6DA4]", bg: "bg-[#EBF2F9]" },
          { label: "Activos", value: stats?.active ?? "—", icon: UserPlus, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "En Proyectos", value: stats?.in_project ?? "—", icon: MapPin, color: "text-purple-600", bg: "bg-purple-50" },
          { label: "Partes Hoy", value: stats?.logs_today ?? 0, icon: ClipboardList, color: "text-amber-600", bg: "bg-amber-50" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200/60 p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow" data-testid={`hr-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0`}><s.icon size={20} className={s.color} /></div>
            <div><p className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">{s.label}</p><p className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "…" : s.value}</p></div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <nav className="flex gap-1 overflow-x-auto pb-1" data-testid="hr-tabs">
        {TABS.filter(tab => !tab.roles || roleAllowed(user?.role, tab.roles)).map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${isActive ? "bg-[#2E6DA4] text-white shadow-sm" : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"}`}
              data-testid={`hr-tab-${tab.id}`}>
              <Icon size={16} />{tab.label}
            </button>
          );
        })}
      </nav>

      {/* Tab content */}
      <div className="animate-in fade-in duration-200">
        {activeTab === "personal" && (
          <PersonalTab workers={workers} loading={loading} projects={projects}
            onNew={() => setWorkerModal({ open: true, item: null })}
            onEdit={w => setWorkerModal({ open: true, item: w })}
            onRefresh={refresh} />
        )}
        {activeTab === "tareas" && <TareasPanel modulo="rrhh" titulo="Tareas y Flujo de Trabajo — RRHH" />}
        {activeTab === "parte_admin" && <ParteDiarioAdminTab workers={workers} projects={projects} />}
        {activeTab === "marcacion" && <MarcacionTab />}
        {activeTab === "parte_obra" && <ParteDiarioObraTab projects={projects} workers={workers} />}
        {activeTab === "adelantos" && <AdelantosDescuentosTab workers={workers} projects={projects} />}
        {activeTab === "desempeno" && <DesempenoTab projects={projects} rules={hrSettings.rules} bonusRate={hrSettings.bonus_conversion_rate_pyg} onRefresh={refresh} />}
        {activeTab === "liquidacion" && <LiquidacionTab workers={workers} projects={projects} />}
        {activeTab === "ips" && <IpsComparacionTab />}
        {activeTab === "config" && <ConfigHRTab rules={hrSettings.rules} bonusRate={hrSettings.bonus_conversion_rate_pyg} onSaved={fetchAll} />}
      </div>

      <WorkerModal open={workerModal.open} onClose={() => setWorkerModal({ open: false, item: null })} onSaved={() => { setWorkerModal({ open: false, item: null }); refresh(); }} worker={workerModal.item} projects={projects} />
    </div>
  );
}
